import React from 'react';
import { RiErrorWarningFill } from 'react-icons/ri';

import Button, { ButtonType } from 'legacy/common/components/button/Button';
import * as toast from 'legacy/common/utils/toast';
import {
  useRefundEventBundleCommand,
  useRefundEventTicketCommand
} from 'legacy/tickets/hooks';
import { queryClient } from 'legacy/common/utils/react-query';

import TicketHolder from './components/TicketHolder';
import { StepProps } from '../../types';

const SubmitRefundStep = ({
  ticket,
  onClose,
  eventId,
  refetchGuestList
}: StepProps) => {
  const {
    error: refundError,
    mutate: requestRefund,
    isLoading: isRefundLoading
  } = useRefundEventTicketCommand();
  const {
    error: refundBundleError,
    mutate: requestRefundBundle,
    isLoading: isRefundBundleLoading
  } = useRefundEventBundleCommand();

  const purchaserName =
    ticket.purchaser.name ??
    `${ticket.purchaser.firstName} ${ticket.purchaser.lastName}`;

  const onRefundSuccess = () => {
    queryClient.invalidateQueries(['search/guestListBreakdown', eventId]);
    queryClient.invalidateQueries(['events/summary', eventId]);
    refetchGuestList();
    onClose();
    if (ticket.bundleProductId) {
      toast.success('Bundle Refunded', `${purchaserName} has been refunded`);
    } else {
      toast.success('Ticket Refunded', `${purchaserName} has been refunded`);
    }
  };

  const onSubmitRefundRequest = () => {
    if (ticket.bundleProductId) {
      requestRefundBundle(
        {
          eventId,
          bundleInstanceId: ticket.bundleInstanceId
        },
        {
          onSuccess: onRefundSuccess,
          onError: () => {
            onClose();
            toast.error('We could not refund the bundle, please try again.');
          }
        }
      );
    } else {
      requestRefund(
        {
          eventId,
          ticketId: ticket.ticketId
        },
        {
          onSuccess: onRefundSuccess,
          onError: () => {
            onClose();
            toast.error('We could not refund the ticket, please try again.');
          }
        }
      );
    }
  };

  return (
    <>
      <div className="p-4">
        <div className="p-4 mb-7 bg-yellow-100 rounded-lg flex gap-2 align-center">
          <RiErrorWarningFill size={24} />
          <p className="font-bold text-base text-warning-dark">
            Guest ticket fees will not be refunded.
          </p>
        </div>
        <div className="flex flex-col gap-4">
          <p className="font-normal text-base">
            Are you sure you want to submit a refund for:
          </p>
          <TicketHolder
            bundleName={ticket.bundleProductName}
            guestPicture={ticket.purchaser.photoUrl}
            guestName={purchaserName}
            tierName={ticket.tierData.name}
          />
        </div>
      </div>
      {(refundError?.message || refundBundleError?.message) && (
        <div className="mt-4 -mb-4 text-red-500 text-center">
          {refundError?.message || refundBundleError?.message}
        </div>
      )}
      <div className="border-t-1 border-solid border-gray-200 p-4">
        <Button
          disabled={isRefundLoading || isRefundBundleLoading}
          onClick={onSubmitRefundRequest}
          type={ButtonType.Danger}
          loading={isRefundLoading || isRefundBundleLoading}
        >
          Refund {ticket.bundleProductId ? 'Bundle' : 'Ticket'}
        </Button>
      </div>
    </>
  );
};

export default SubmitRefundStep;
